import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { PenTool, RotateCcw, Check } from 'lucide-react';

const SimpleSignaturePad = ({ onSignatureChange, initialSignature, disabled = false }) => {
  const canvasRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (initialSignature) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setIsEmpty(false);
      };
      img.src = initialSignature;
    }
  }, [initialSignature]);

  const getEventPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e) => {
    if (disabled) return;
    
    e.preventDefault();
    setIsDrawing(true);
    const pos = getEventPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsEmpty(false);
  };

  const draw = (e) => {
    if (!isDrawing || disabled) return;
    
    e.preventDefault();
    const pos = getEventPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = (e) => {
    if (!isDrawing || disabled) return;
    
    e.preventDefault();
    setIsDrawing(false);
    
    // Export signature
    const canvas = canvasRef.current;
    const signature = canvas.toDataURL();
    if (onSignatureChange) {
      onSignatureChange(signature);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
    if (onSignatureChange) {
      onSignatureChange('');
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <PenTool className="w-5 h-5 text-emerald-600" />
          Digitale Signatur (Einfach)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative">
          <canvas
            ref={canvasRef}
            className={`border-2 border-dashed border-slate-300 rounded-lg w-full h-40 ${
              disabled ? 'cursor-not-allowed opacity-50' : 'cursor-crosshair'
            }`}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            disabled={disabled}
          />
          {isEmpty && !disabled && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-slate-400 text-sm">Hier unterschreiben</p>
            </div>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={clearSignature}
            disabled={disabled || isEmpty}
            className="flex-1 gap-2"
            data-testid="signature-clear-button"
          >
            <RotateCcw className="w-4 h-4" />
            Löschen
          </Button>
          {!isEmpty && (
            <Button
              type="button"
              variant="default"
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              disabled
            >
              <Check className="w-4 h-4" />
              Signiert
            </Button>
          )}
        </div>
        
        <div className="text-xs text-slate-500 text-center">
          Verwenden Sie Ihren Finger oder Stylus zum Signieren (Einfache Version)
        </div>
      </CardContent>
    </Card>
  );
};

export default SimpleSignaturePad;